import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Users API service
 */
export const UserApi = {
  /**
   * Get all users (Admin only)
   * @returns {Promise<Response>} All users response
   */
  getUsers: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users`, {
      method: 'GET',
    });
  },

  /**
   * Get user by ID (Admin only)
   * @param {string|number} id - User ID
   * @returns {Promise<Response>} User response
   */
  getUser: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Get user by email (Admin only)
   * @param {string} email - User email
   * @returns {Promise<Response>} User response
   */
  getUserByEmail: async (email) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/email/${encodeURIComponent(email)}`, {
      method: 'GET',
    });
  },

  /**
   * Create user
   * @param {Object} data - User data
   * @returns {Promise<Response>} Create response
   */
  createUser: async (data) => {
    // Convert to match backend field names (camelCase for Spring)
    const formattedData = {
      organizationId: data.organization_id || data.organizationId,
      email: data.email,
      firstName: data.first_name || data.firstName,
      lastName: data.last_name || data.lastName,
      password: data.password
    };
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formattedData),
    });
  },

  /**
   * Update user (Admin only)
   * @param {string|number} id - User ID
   * @param {Object} data - Updated user data
   * @returns {Promise<Response>} Update response
   */
  updateUser: async (id, data) => {
    // Convert to match backend field names
    const formattedData = {
      ...data,
      first_name: data.first_name || data.firstName,
      last_name: data.last_name || data.lastName,
      is_active: data.is_active ?? data.isActive
    };
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formattedData),
    });
  },

  /**
   * Delete user (Admin only)
   * @param {string|number} id - User ID
   * @returns {Promise<Response>} Delete response
   */
  deleteUser: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Send invitation to a user (Admin only)
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.firstName
   * @param {string} params.lastName
   * @returns {Promise<Response>}
   */
  sendInvitation: async ({ email, firstName, lastName }) => {
    const url = new URL(`${DATA_SERVICE_BASE_URL}/api/users/send-invitation`);
    url.searchParams.set('email', email);
    url.searchParams.set('firstName', firstName);
    url.searchParams.set('lastName', lastName);

    return fetchWithAuth(url.toString(), {
      method: 'POST',
    });
  },

  /**
   * Get all users (Admin only) with pagination, sorting, and search
   * Note: UI page is 1-based; API expects 0-based.
   * @param {{page?: number, size?: number, sortBy?: 'userId'|'username'|'roles'|'recentLogin', sortDir?: 'asc'|'desc', search?: string, signal?: AbortSignal}} params
   */
  getAllUsers: async ({ page = 1, size = 10, sortBy, sortDir, search, signal } = {}) => {
    const url = new URL(`${DATA_SERVICE_BASE_URL}/api/users`);
    const zeroBasedPage = Math.max(0, Number(page) - 1);
    url.searchParams.set('page', zeroBasedPage);
    url.searchParams.set('size', size);
    if (sortBy) url.searchParams.set('sortBy', sortBy);
    if (sortDir) url.searchParams.set('sortDir', sortDir);
    if (search && search.trim()) url.searchParams.set('search', search.trim());
    return fetchWithAuth(url.toString(), { method: 'GET', signal });
  },

  /**
   * Impersonate a user (Admin only).
   * Returns a session for the target user with an impersonatedBy claim.
   * @param {number} targetUserId - ID of the user to impersonate
   * @returns {Promise<Response>} Impersonation session response
   */
  impersonateUser: async (targetUserId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/impersonate/${targetUserId}`, {
      method: 'POST',
    });
  },

  // ── Organization Memberships (Admin only) ─────────────────────────────

  /** GET /api/users/{userId}/organization-memberships */
  getUserMemberships: async (userId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/organization-memberships`, { method: 'GET' }),

  /** POST /api/users/{userId}/organization-memberships */
  addUserToOrganization: async (userId, organizationId, orgRole = 'org_member') =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/organization-memberships`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId, orgRole }),
    }),

  /** DELETE /api/users/{userId}/organization-memberships/{membershipId} */
  removeUserFromOrganization: async (userId, membershipId) =>
    fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/organization-memberships/${membershipId}`, {
      method: 'DELETE',
    }),
};